import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const Contact = () => {
    const [siteSettings, setSiteSettings] = useState({
        companyName: "VishwaVoyagerz",
        address: "Bhoomi Mall, 318, plot 8, Sector 15, CBD Belapur, Belapur, Maharashtra 400614",
        phone: "+91 976290455",
        email: "info@vishwavoyagerz.com",
        facebookUrl: "https://www.facebook.com/share/1ArjypHZtG/",
        instagramUrl: "https://www.instagram.com/_vishwa_voyagerz_?igsh=MWxoNnM1ZzlqNnd4aA=="
    });
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await axios.get('/api/site-settings');
                setSiteSettings(response.data);
            } catch (error) {
                console.error("Error fetching site settings:", error);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e) => {
        const { id, value } = e.target;
        // Map form IDs to state keys (contactName -> name, etc.)
        const key = id.replace('contact', '').toLowerCase();
        setFormData(prev => ({
            ...prev,
            [key]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            await axios.post('/api/contacts', formData);
            setStatus({ type: 'success', message: 'Thank you for contacting us! We will get back to you shortly.' });
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: ''
            });
        } catch (error) {
            console.error('Error submitting contact form:', error);
            setStatus({ type: 'error', message: 'Something went wrong. Please try again later.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Navigation />

            {/* Hero Section */}
            <section className="relative mt-20 overflow-hidden bg-gradient-to-r from-brand-blue/80 to-brand-teal/60 text-white py-24">
                <div className="blob bg-brand-green w-96 h-96 top-20 left-10"></div>
                <div className="blob bg-brand-teal w-80 h-80 bottom-20 right-20" style={{ animationDelay: '2s' }}></div>

                <div className="absolute inset-0">
                    <img src="https://images.unsplash.com/photo-1596524430615-b46475ddff6e?w=1920&h=600&fit=crop" alt="Contact Us" className="w-full h-full object-cover opacity-20" />
                </div>

                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight">Contact Us</h1>
                    <p className="text-xl sm:text-2xl text-gray-200 max-w-3xl mx-auto">Have questions? We're here to help you plan your perfect trip.</p>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="py-20 bg-gray-50 relative overflow-hidden">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                        {/* Contact Info */}
                        <div className="space-y-8">
                            <div className="bg-white rounded-2xl p-8 shadow-lg hover-lift">
                                <h3 className="text-2xl font-bold text-gray-800 mb-6">Get in Touch</h3>
                                <div className="space-y-6">
                                    <div className="flex items-start">
                                        <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                                            <i className="fas fa-map-marker-alt text-brand-teal text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 mb-1">Our Location</h4>
                                            <p className="text-gray-600">{siteSettings.address}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                                            <i className="fas fa-phone text-brand-teal text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 mb-1">Phone Number</h4>
                                            <p className="text-gray-600">{siteSettings.phone}</p>
                                            <p className="text-gray-500 text-sm mt-1">Mon-Fri 9am-6pm</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start">
                                        <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mr-4 shrink-0">
                                            <i className="fas fa-envelope text-brand-teal text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-gray-800 mb-1">Email Address</h4>
                                            <p className="text-gray-600">{siteSettings.email}</p>
                                            <p className="text-gray-500 text-sm mt-1">We rely within 24 hours</p>
                                        </div>
                                    </div>

                                    {/* Social Media */}
                                    {(siteSettings.facebookUrl || siteSettings.instagramUrl) && (
                                        <div className="flex items-center gap-3 pt-2">
                                            <div className="w-12 h-12 bg-brand-teal/10 rounded-full flex items-center justify-center mr-1 shrink-0">
                                                <i className="fas fa-share-alt text-brand-teal text-xl"></i>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 mb-2">Follow Us</h4>
                                                <div className="flex gap-3">
                                                    {siteSettings.facebookUrl && (
                                                        <a href={siteSettings.facebookUrl} target="_blank" rel="noopener noreferrer"
                                                            className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 hover:scale-110 transition transform"
                                                            title="Facebook">
                                                            <i className="fab fa-facebook-f"></i>
                                                        </a>
                                                    )}
                                                    {siteSettings.instagramUrl && (
                                                        <a href={siteSettings.instagramUrl} target="_blank" rel="noopener noreferrer"
                                                            className="w-10 h-10 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 text-white rounded-full flex items-center justify-center hover:scale-110 transition transform"
                                                            title="Instagram">
                                                            <i className="fab fa-instagram"></i>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-brand-teal to-brand-cyan rounded-2xl p-8 text-white shadow-lg">
                                <h3 className="text-2xl font-bold mb-4">FAQ</h3>
                                <p className="mb-6">Have common questions? Check out our frequently asked questions before sending a message.</p>
                                <button className="bg-white text-brand-teal px-6 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105">
                                    Visit Help Center
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <div className="bg-white rounded-2xl p-8 shadow-2xl">
                            <h3 className="text-3xl font-bold text-gray-800 mb-6">Send us a Message</h3>

                            {status.message && (
                                <div className={`mb-6 p-4 rounded-lg ${status.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {status.message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <label className="block text-gray-700 font-semibold mb-2">Your Name *</label>
                                        <input
                                            type="text"
                                            id="contactName"
                                            value={formData.name}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-semibold mb-2">Email Address *</label>
                                        <input
                                            type="email"
                                            id="contactEmail"
                                            value={formData.email}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal transition"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <label className="block text-gray-700 font-semibold mb-2">Phone Number</label>
                                        <input
                                            type="tel"
                                            id="contactPhone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-gray-700 font-semibold mb-2">Subject *</label>
                                        <input
                                            type="text"
                                            id="contactSubject"
                                            value={formData.subject}
                                            onChange={handleChange}
                                            required
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal transition"
                                        />
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                                    <textarea
                                        id="contactMessage"
                                        rows="5"
                                        value={formData.message}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-teal transition"
                                        placeholder="How can we help you?"
                                    ></textarea>
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {isSubmitting ? (
                                        <span><i className="fas fa-spinner fa-spin mr-2"></i>Sending...</span>
                                    ) : (
                                        <span><i className="fas fa-paper-plane mr-2"></i>Send Message</span>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </>
    );
};

export default Contact;
